/* eslint-disable @angular-eslint/no-empty-lifecycle-method */
import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { AutoUnsubscribe } from 'ngx-auto-unsubscribe';
import { Observable, Subscription } from 'rxjs';
import { filter, take, tap } from 'rxjs/operators';

import { ILoggedHomeAppState } from '@hub-workspace/apps/logged-home/src/app/shared/interfaces/logged-home-app-state.interface';
import { CustomerWalletAction } from '../../../../../state-management/customer-wallet/customer-wallet.action';
import { ICustomerWallet, ICustomerWalletCategory } from '../../interfaces/customer-wallet.interface';
import { TrackingActions } from '@toro/common/shared/state-management/tracking/tracking.action';
import {
    ModalAccessFullWalletComponent
} from '@hub-workspace/apps/logged-home/src/app/shared/modules/customer-wallet/components/modal-full-wallet/modal-access-full-wallet.component';
import { DialogsService } from '@toro/common/shared/services/dialogs.service';
import { SettingsService } from '@toro/common/shared/services/settings/settings.service';
import {
  ModalCriptoAccessComponent
} from '@hub-workspace/apps/logged-home/src/app/shared/modules/customer-wallet/components/modal-cripto-access/modal-cripto-access.component';
import {
    WALLET_FULL_ACCESS_ALERT_LINK_LABEL,
    WALLET_FULL_ACCESS_ALERT_TEXT_DEFAULT,
    WALLET_FULL_ACCESS_ALERT_TEXT_SANTANDER,
} from '@hub-workspace/apps/logged-home/src/app/shared/modules/customer-wallet/components/wallet-full-access-alert/wallet-full-access-alert.constants';
import { OneViewExperienceVariant } from '@hub-workspace/apps/logged-home/src/app/shared/modules/customer-wallet/services/oneview-experience-variant/oneview-experience-variant.util';
import { OneViewExperienceVariantService } from '@hub-workspace/apps/logged-home/src/app/shared/modules/customer-wallet/services/oneview-experience-variant/oneview-experience-variant.service';

@AutoUnsubscribe()
@Component({
    selector: 'customer-wallet-details',
    templateUrl: './customer-wallet-details.component.html',
    styleUrls: ['./customer-wallet-details.component.scss'],
})
export class CustomerWalletDetailsComponent implements OnDestroy, OnInit, AfterViewInit {

    @ViewChild('pageTop', { static: true })
    public pageElement: ElementRef<HTMLDivElement>;

    public wallet: ICustomerWallet = null;
    public selectedCategory: ICustomerWalletCategory =   null;
    public disposableSubscriptions: Subscription;
    public alertWalletType: string = '';
    public blockedGroupName: string = '';

    public oneViewExperienceVariant$: Observable<OneViewExperienceVariant>;
    private oneViewExperienceVariant: OneViewExperienceVariant | null = null;

    constructor(
        private store: Store<ILoggedHomeAppState>,
        private router: Router,
        private activatedRoute: ActivatedRoute,
        private dialogsService: DialogsService,
        private oneViewExperienceVariantService: OneViewExperienceVariantService,
        settingService: SettingsService,
    ) {
        this.blockedGroupName = settingService.getConfigurationKeyValue('groupNameEnv');
    }

    public ngOnInit() {
        this.disposableSubscriptions = this.store
            .select((state) => state.customerWallet)
            .pipe(
                tap(({ wallet, pending, isLoaded }) => {
                    if (!wallet && !pending && !isLoaded) {
                        this.store.dispatch({
                            type: CustomerWalletAction.getCustomerWallet
                        })
                    }
                }),
                filter(({ isLoaded }) => isLoaded)
            )
            .subscribe(({ wallet }) => {
                if (wallet) {
                    this.wallet = wallet;
                    const { category } = this.activatedRoute.snapshot.queryParams;
                    return this.navigateToCategory(this.wallet.categories.find(({ slug }) => slug === category));
                }

                this.router.navigateByUrl('/')
            });

        this.oneViewExperienceVariant$ = this.oneViewExperienceVariantService.variant$.pipe(
            take(1),
            tap((variant) => this.oneViewExperienceVariant = variant),
        );
    }

    public ngAfterViewInit(): void {
        this.pageElement.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    public ngOnDestroy() {
        // Não remover, necessário para o funcionamento correto do @AutoUnsubscribe
    }

    public openStrategicalClassPage() {
        this.sendTracking("Element Clicked", {
            'Product Area Origin': 'Detalhes da carteira',
            'Time Responsável': "Mezzo",
            'Element': 'FAQ classes estratégicas',
        });

        window.open('https://ajuda.toroinvestimentos.com.br/hc/pt-br/articles/10119145835291-Acompanhamento-de-aloca%C3%A7%C3%A3o-por-Classes-Estrat%C3%A9gicas');
    }

    public onSelectCategory(category: ICustomerWalletCategory) {
        this.navigateToCategory(category);

        this.sendTracking("Element Clicked", {
            'Product Area Origin': 'Detalhes da carteira',
            'Time Responsável': "Mezzo",
            'Element': category.name,
            'Group': 'Tags classes estratégicas',
        });
    }

    private sendTracking(event: string, breakdowns: any) {
        this.store.dispatch({
            type: TrackingActions.trackEvents,
            payload: { type: event, events: breakdowns },
        });
    }

    private navigateToCategory(category: ICustomerWalletCategory) {
        // `category` is undefined whenever the URL carries no (or an unknown)
        // `?category=` param — resolve the fallback *before* dereferencing it.
        this.selectedCategory = category || this.wallet.categories[0];

        this.alertWalletType = this.selectedCategory.groups.some(group => group.name === this.blockedGroupName) ? this.blockedGroupName : '';

        this.router.navigate([this.router.url.split('?')[0]], { queryParams: { category: this.selectedCategory.slug } });
    }

    public openModalAccessFullWallet(){
        switch (this.alertWalletType) {
          case 'Criptoativos':
            this.dialogsService.showComponnent(ModalCriptoAccessComponent);
            break;
          default:
            this.dialogsService.showComponnent(ModalAccessFullWalletComponent);
            break;
        }

        const isNewExperience = this.oneViewExperienceVariant === 'new';
        const alertText = isNewExperience
            ? WALLET_FULL_ACCESS_ALERT_TEXT_SANTANDER
            : WALLET_FULL_ACCESS_ALERT_TEXT_DEFAULT;

        this.sendTracking("Element Clicked", {
            'Product Area Origin': 'Detalhes da carteira',
            'Time Responsável': "Mezzo",
            'Element': 'Link Saiba mais',
            'Group': 'Alert Carteira',
            'Step': isNewExperience ? 'Não se aplica' : 'Detalhes da Carteira',
            'Scenario': 'Exclusivo',
            'Text': `${alertText} ${WALLET_FULL_ACCESS_ALERT_LINK_LABEL}`
        });
    }
}
